/**
 * RouteRise — the route-level entrance transition (24 Jul, "the site doesn't
 * feel seamless" finding). Reuses packages/ui's .mdui-rise verbatim: the same
 * translateY+opacity grammar already built for SkeletonToContent's
 * loading→content swap (ui.css's own header calls it "the RISE grammar," one
 * owner) — not a new animation invented for this.
 *
 * WHY NOT A PLAIN `<div key={location} className="mdui-rise">`: .mdui-rise's
 * keyframe ends on `transform: translateY(0)`, and the animation's `both`
 * fill-mode keeps that transform applied indefinitely once it finishes. Any
 * non-none transform on an ancestor — even a no-op translateY(0) — creates a
 * new containing block for `position: fixed` descendants (CSS spec, not a
 * bug), so every fixed-position overlay inside a routed screen (cabinet/
 * centre/comms/feed/firstnight/locker/player/rewards ALL use position:fixed
 * for toasts/sheets/scrims) would silently stop covering the viewport the
 * moment its screen's entrance finished — invisible until you actually open
 * one of those overlays.
 *
 * Fix: the class (and its transform) is only present for the animation's own
 * duration, then this drops to a plain unstyled wrapper — the entrance plays
 * once per navigation, nothing is ever left transformed while a screen sits
 * idle. Duration mirrors mdui-rise's own (--md-time-base = 300ms, MOTION.md);
 * prefers-reduced-motion clamps both the CSS animation AND this timer via the
 * shared --md-time-reduced-cap read at runtime, so the fixed-position window
 * shrinks with it too, never staying open longer than the animation does.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/** Mirrors .mdui-rise, which is MOTION.md's `rise` beat: 300ms. */
const RISE_MS = 300;

function riseDurationMs(): number {
  if (typeof window === "undefined") return RISE_MS;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!reduced) return RISE_MS;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--md-time-reduced-cap");
  const parsed = Number.parseFloat(raw) * 1000;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 150;
}

export function RouteRise({ routeKey, children }: { routeKey: string; children: ReactNode }): JSX.Element {
  const [rising, setRising] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRising(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRising(false), riseDurationMs());
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  return <div className={rising ? "mdui-rise" : undefined}>{children}</div>;
}
